import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance, { isAxiosError } from "@/utils/axiosInstance";

interface FetchEstablishmentsParams {
  page?: number;
  per_page?: number;
  search?: string;
  status?: "active" | "inactive" | "pending" | "suspended";
  plan_id?: number;
  city?: string;
  state?: string;
}

interface UpdateEstablishmentParams {
  id: number;
  data: {
    name?: string;
    email?: string;
    plan_id?: number;
    is_active?: boolean;
    phone_whatsapp?: string;
    landline?: string;
    cpf_cnpj?: string;
    city?: string;
    state?: string;
    country?: string;
    password?: string;
    password_confirmation?: string;
  };
}

interface CreateEstablishmentParams {
  name: string;
  email: string;
  plan_id: number;
  is_active?: boolean;
  phone_whatsapp?: string;
  landline?: string;
  cpf_cnpj?: string;
  city?: string;
  state?: string;
  country?: string;
  password?: string;
  password_confirmation?: string;
}

// Buscar estabelecimentos (para Super Admin)
export const fetchEstablishments = createAsyncThunk(
  "establishments/fetchEstablishments",
  async (params: FetchEstablishmentsParams, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/admin/establishments", {
        params,
      });
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        const errorMessage = error?.response?.data as {
          message?: string;
        };
        return rejectWithValue({
          error: errorMessage?.message || error.message,
        });
      } else {
        return rejectWithValue({
          error: "Erro ao carregar estabelecimentos",
        });
      }
    }
  },
);

// Ativar estabelecimento
export const activateEstablishment = createAsyncThunk(
  "establishments/activate",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/admin/establishments/${id}/activate`,
      );
      return { ...response.data, id, status: "active" };
    } catch (error: any) {
      if (isAxiosError(error)) {
        const errorMessage = error?.response?.data as {
          message?: string;
        };
        return rejectWithValue({
          error: errorMessage?.message || "Erro ao ativar estabelecimento",
          status: error.response?.status,
        });
      }

      return rejectWithValue({
        error: "Erro inesperado ao ativar estabelecimento",
      });
    }
  },
);

// Desativar estabelecimento
export const deactivateEstablishment = createAsyncThunk(
  "establishments/deactivate",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/admin/establishments/${id}/deactivate`,
      );
      return { ...response.data, id, status: "inactive" };
    } catch (error: any) {
      if (isAxiosError(error)) {
        const errorMessage = error?.response?.data as {
          message?: string;
        };
        return rejectWithValue({
          error: errorMessage?.message || "Erro ao desativar estabelecimento",
          status: error.response?.status,
        });
      }

      return rejectWithValue({
        error: "Erro inesperado ao desativar estabelecimento",
      });
    }
  },
);

// Função helper para toggle (mantendo compatibilidade)
export const toggleEstablishmentStatus = createAsyncThunk(
  "establishments/toggleStatus",
  async (
    { id, status }: { id: number; status: "active" | "inactive" },
    { dispatch, rejectWithValue },
  ) => {
    try {
      if (status === "active") {
        return await dispatch(activateEstablishment(id)).unwrap();
      } else {
        return await dispatch(deactivateEstablishment(id)).unwrap();
      }
    } catch (error: any) {
      return rejectWithValue({
        error: error.error || "Erro ao alterar status do estabelecimento",
      });
    }
  },
);

// Atualizar estabelecimento
export const updateEstablishment = createAsyncThunk(
  "establishments/updateEstablishment",
  async ({ id, data }: UpdateEstablishmentParams, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(
        `/admin/establishments/${id}`,
        data,
      );
      return response.data;
    } catch (error: any) {
      if (isAxiosError(error)) {
        const errorMessage = error?.response?.data as {
          message?: string;
        };
        return rejectWithValue({
          error: errorMessage?.message || "Erro ao atualizar estabelecimento",
          status: error.response?.status,
        });
      }

      return rejectWithValue({
        error: "Erro inesperado ao atualizar estabelecimento",
      });
    }
  },
);

// Criar estabelecimento
export const createEstablishment = createAsyncThunk(
  "establishments/createEstablishment",
  async (data: CreateEstablishmentParams, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/admin/establishments`, data);
      return response.data;
    } catch (error: any) {
      if (isAxiosError(error)) {
        const errorMessage = error?.response?.data as {
          message?: string;
        };
        return rejectWithValue({
          error: errorMessage?.message || "Erro ao criar estabelecimento",
          status: error.response?.status,
        });
      }

      return rejectWithValue({
        error: "Erro inesperado ao criar estabelecimento",
      });
    }
  },
);
